/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta


  // Definición del tipo para la respuesta del blog
  interface Blog {
    _id: string;
    title: string;
    excerpt: string;
    details: string;
    author: string;
    readTime: number;
    tags: string[];
    category: string;
    publicationDate: string;
    images: string[];
    authorSpecialty: string;
    relatedPosts: string[];
    createdAt: string;
    authorName:string
  }

  

// Función para crear un blog
const createBlog = async (blogData: {
    title: string;
    userId: string;
    excerpt: string;
    details: string;
    author: string;
    authorSpecialty?: string;
    readTime: number;
    tags?: string[];
    category: string;
    publicationDate?: Date;
    relatedPosts?: string[];
    images?: string[];
  }) => {
    const response = await axiosInstance.post('/blogs/create', blogData);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para crear un blog
  export const useCreateBlog = () => {
    const mutation = useMutation({
      mutationFn: createBlog, // Función para crear el blog
      onSuccess: () => {
        toast.success('Blog creado con éxito.');
      },
      onError: (error: any) => {
        // Verifica la existencia de un mensaje de error en la respuesta
        const errorMessage = error?.response?.data?.message || 'Error desconocido';
        toast.error(`Error al crear el blog: ${errorMessage}`);
        console.error('Error al crear el blog:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la creación fue exitosa
      error: mutation.error?.response?.data?.message || mutation.error?.message, // Captura el error correctamente
    };
  };

  

  // Función para obtener todos los blogs
const fetchAllBlogs = async () => {
    const response = await axiosInstance.get('/blogs/listing/all');
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para listar todos los blogs
  export const useGetAllBlogs = () => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['blogs'], // Identifica la consulta en React Query
      queryFn: async () => {
        try {
          return await fetchAllBlogs();
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
          toast.error(`Error al obtener los blogs: ${errorMessage}`);
          console.error('Error al obtener los blogs:', error);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Evita que los datos se recarguen al cambiar de ventana
    });
  
    return {
      blogs: data,  // Lista de blogs obtenidos
      isLoading,    // Estado de carga
      isError,      // Indica si hubo un error
      error,        // Detalle del error (si existe)
    };
  };
  



// Función para obtener un blog por su ID
const fetchBlogById = async (blogId: string): Promise<Blog> => {
  const response = await axiosInstance.get(`/blogs/${blogId}`);
  return response.data.data.blog; // Accedemos a la estructura de datos correcta (data.blog)
};

// Hook tipificado para obtener un blog por su ID
export const useGetBlogById = (blogId: string) => {
  const { data, error, isLoading, isError } = useQuery<Blog, Error>({
    queryKey: ['blog', blogId], // Identifica la consulta en React Query con el ID del blog
    queryFn: async () => {
      try {
        return await fetchBlogById(blogId);
      } catch (error) {
        const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
        toast.error(`Error al obtener el blog: ${errorMessage}`);
        console.error('Error al obtener el blog:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Evita que los datos se recarguen al cambiar de ventana
  });

  return {
    blog: data,      // Datos del blog obtenido
    isLoading,       // Estado de carga
    isError,         // Indica si hubo un error
    error,           // Detalle del error (si existe)
  };
};


  // Función para obtener los blogs de un usuario
const fetchUserBlogs = async () => {
    const response = await axiosInstance.get('/blogs/');
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para listar los blogs de un usuario
  export const useGetUserBlogs = () => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['user-blogs'], // Identifica la consulta en React Query
      queryFn: async () => {
        try {
          return await fetchUserBlogs();
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
          toast.error(`Error al obtener los blogs del usuario: ${errorMessage}`);
          console.error('Error al obtener los blogs del usuario:', error);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Evita que los datos se vuelvan a obtener al cambiar de ventana
    });
  
    return {
      userBlogs: data,    // Lista de blogs del usuario obtenidos
      isLoading,          // Estado de carga
      isError,            // Indica si hubo un error
      error,              // Detalle del error (si existe)
    };
  };
  


  // Función para eliminar un blog
const deleteBlog = async (blogId: string) => {
    const response = await axiosInstance.delete(`/blogs/${blogId}`);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para eliminar un blog
  export const useDeleteBlog = () => {
    const mutation = useMutation({
      mutationFn: deleteBlog, // Función para eliminar el blog
      onSuccess: () => {
        toast.success('Blog eliminado con éxito.');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || 'Error desconocido';
        toast.error(`Error al eliminar el blog: ${errorMessage}`);
        console.error('Error al eliminar el blog:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
    };
  };
  

  // Función para obtener los posts relacionados de un blog
const fetchRelatedPosts = async (blogId: string) => {
    const response = await axiosInstance.get(`/blogs/${blogId}/related-posts`);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para obtener los posts relacionados de un blog
  export const useGetRelatedPosts = (blogId: string) => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['related-posts', blogId], // Identifica la consulta en React Query
      queryFn: async () => {
        try {
          return await fetchRelatedPosts(blogId); // Se pasa el blogId para obtener los posts relacionados
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
          toast.error(`Error al obtener los posts relacionados del blog: ${errorMessage}`);
          console.error('Error al obtener los posts relacionados del blog:', errorMessage);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Evita que los datos se vuelvan a obtener al cambiar de ventana
    });
  
    return {
      relatedPosts: data,  // Lista de posts relacionados obtenidos
      isLoading,           // Estado de carga
      isError,             // Indica si hubo un error
      error,               // Detalle del error (si existe)
    };
  };
  


// Función para actualizar un blog
const updateBlog = async ({
  blogId,
  blogData,
}: {
  blogId: string;
  blogData: any;
}) => {
  const response = await axiosInstance.put(`/blogs/${blogId}`, blogData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para actualizar un blog
export const useUpdateBlog = () => {
  const mutation = useMutation({
    mutationFn: updateBlog, // Función para actualizar el blog
    onSuccess: () => {
      toast.success('Blog actualizado con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error desconocido';
      toast.error(`Error al actualizar el blog: ${errorMessage}`);
      console.error('Error al actualizar el blog:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
  };
};


const markBlogAsFavorite = async (blogId: string) => {
  const response = await axiosInstance.post('/blogs/mark-as-favorite', { blogId });
  return response.data;
};

export const useMarkBlogAsFavorite = () => {
  const mutation = useMutation({
    mutationFn: markBlogAsFavorite,
    onSuccess: () => {
      toast.success("Blog marcado como favorito con éxito.");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error desconocido";

      toast.error(errorMessage);
      console.error("Error al marcar como favorito:", errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};
